/**
 * HustleEmergencyPage.jsx — big-button emergency dial pad.
 *
 * Reachable by:
 *   • Wake phrase "Hustle Emergency"
 *   • Universal link hustleapp://emergency
 *   • Direct route /hustle-emergency
 *
 * Designed for stress: huge tap targets, no clutter, no auth required.
 */
import { useEffect, useState } from "react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { MapPin, Copy, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = "hustle_emergency_address_v1";

const NUMBERS = [
  {
    label: "Police / Fire / Ambulance",
    sub:   "Australia — all emergencies",
    dial:  "000",
    color: "bg-rose-600 hover:bg-rose-500",
    icon:  "🚨",
  },
  {
    label: "State Emergency Service (SES)",
    sub:   "Storm, flood, urgent help (not life-threatening)",
    dial:  "132500",
    color: "bg-orange-500 hover:bg-orange-400",
    icon:  "⚠️",
  },
  {
    label: "Poisons Information",
    sub:   "Suspected poisoning, overdose, snake bite",
    dial:  "131126",
    color: "bg-amber-500 hover:bg-amber-400",
    icon:  "🧪",
  },
  {
    label: "Lifeline (Crisis support)",
    sub:   "24/7 crisis & suicide prevention support",
    dial:  "131114",
    color: "bg-sky-600 hover:bg-sky-500",
    icon:  "💬",
  },
  {
    label: "Marine Rescue NSW",
    sub:   "Boating distress on water (NSW)",
    dial:  "0294500600",
    color: "bg-cyan-600 hover:bg-cyan-500",
    icon:  "⛵",
  },
  {
    label: "Police Assistance Line (non-urgent)",
    sub:   "Report a crime that's already happened",
    dial:  "131444",
    color: "bg-slate-600 hover:bg-slate-500",
    icon:  "👮",
  },
];

export default function HustleEmergencyPage() {
  const [address, setAddress] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Light haptic to signal arrival (Capacitor on native, no-op on web)
    try {
      import("@capacitor/haptics").then(({ Haptics, ImpactStyle }) => {
        Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
      }).catch(() => {});
    } catch { /* haptics unsupported */ }
  }, []);

  const handleAddressChange = (val) => {
    setAddress(val);
    try {
      if (val) localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage unavailable (private mode) */ }
  };

  const copyAddress = async () => {
    if (!address?.label) return;
    try {
      await navigator.clipboard.writeText(address.label);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-6"
         data-testid="hustle-emergency">
      <header className="max-w-2xl mx-auto mb-4">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
          Hustle Emergency
        </h1>
        <p className="text-slate-400 mt-2 text-sm">
          Tap any button to dial. In a true life-threatening emergency, call <strong>000</strong> first.
        </p>
      </header>

      {/* Address panel — read this to the operator */}
      <section className="max-w-2xl mx-auto mb-5 rounded-2xl bg-slate-900/80 border border-slate-800 p-4"
               data-testid="emergency-address-panel">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">
            Your address (read this to the operator)
          </h2>
        </div>
        {address?.label ? (
          <div className="flex items-start gap-3 flex-wrap" data-testid="emergency-address-saved">
            <div className="flex-1 min-w-0">
              <p className="text-lg md:text-xl font-semibold text-white break-words">
                {address.label}
              </p>
              {address.lat && address.lng ? (
                <p className="text-xs text-slate-400 mt-1">
                  GPS: {Number(address.lat).toFixed(5)}, {Number(address.lng).toFixed(5)}
                </p>
              ) : null}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={copyAddress}
                data-testid="emergency-address-copy"
                className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
              >
                {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                onClick={() => handleAddressChange(null)}
                data-testid="emergency-address-clear"
                className="text-xs px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
              >
                Change
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-slate-950/60 rounded-lg p-2 border border-slate-800" data-testid="emergency-address-picker">
            <AddressAutocomplete
              value={null}
              onChange={handleAddressChange}
              testIdPrefix="emergency-addr"
              placeholder="Save your address — kept on this device only"
            />
            <p className="text-[11px] text-slate-500 mt-2">
              Saved locally on this device. Helps you read your address to the operator without thinking.
            </p>
          </div>
        )}
      </section>

      <main className="max-w-2xl mx-auto grid gap-4">
        {NUMBERS.map((n, i) => (
          <a
            key={n.dial}
            href={`tel:${n.dial}`}
            data-testid={`emergency-dial-${n.dial}`}
            className={`block rounded-2xl ${n.color} active:scale-[0.98] transition-transform px-6 py-7 shadow-2xl`}>
            <div className="flex items-center gap-4">
              <span className="text-5xl shrink-0" aria-hidden>{n.icon}</span>
              <div className="min-w-0">
                <p className="text-2xl md:text-3xl font-extrabold leading-tight">
                  {n.dial.replace(/(\d{3})(\d{3})(\d{3})?/, "$1 $2 $3").trim()}
                </p>
                <p className="text-base md:text-lg font-semibold mt-0.5">{n.label}</p>
                <p className="text-xs md:text-sm opacity-90 mt-0.5">{n.sub}</p>
              </div>
            </div>
          </a>
        ))}
      </main>

      <footer className="max-w-2xl mx-auto mt-8 text-xs text-slate-500 text-center">
        For non-urgent local issues, try Hustle Directory.
      </footer>
    </div>
  );
}
